/**
 * The Daily — five questions, the same five for everybody, once a day.
 *
 * The selection is a pure function of the date. No server, no coordination:
 * two people on opposite sides of the world open the app and are handed the
 * same five questions, because both derived them from the same number. That is
 * what makes the result worth sharing — a score is only interesting if it is a
 * score at the same thing.
 *
 * It also means this works offline and costs nothing to run, which a
 * server-issued daily would not.
 */

import { QUESTIONS } from '../data/questions.js';

export const DAILY_COUNT = 5;

/** Where the numbering starts, so day one is day one rather than 20,000. */
const EPOCH = Date.UTC(2026, 0, 1);

/**
 * Which day it is, in whole days since the epoch.
 *
 * Deliberately UTC. Local midnight would mean New Zealand and California are
 * on different puzzles for most of the day, and someone flying east would be
 * handed a second go at the same one.
 */
export function dailyNumber(now = new Date()) {
  const midnight = Date.UTC(now.getUTCFullYear(), now.getUTCMonth(), now.getUTCDate());
  return Math.floor((midnight - EPOCH) / 86_400_000) + 1;
}

/** Milliseconds until the next puzzle. Drives the countdown. */
export function millisUntilNext(now = new Date()) {
  const next = Date.UTC(now.getUTCFullYear(), now.getUTCMonth(), now.getUTCDate() + 1);
  return next - now.getTime();
}

/** A small deterministic generator — same seed, same sequence, everywhere. */
function mulberry(seed) {
  let a = seed >>> 0;
  return () => {
    a = (a + 0x6d2b79f5) >>> 0;
    let t = Math.imul(a ^ (a >>> 15), 1 | a);
    t = (t + Math.imul(t ^ (t >>> 7), 61 | t)) ^ t;
    return ((t ^ (t >>> 14)) >>> 0) / 4294967296;
  };
}

/**
 * The day's five.
 *
 * Drawn across difficulties on purpose: two easy, two medium, one hard. A
 * daily that is all hard is one most people fail and stop opening; all easy and
 * there is nothing to report. The mix means almost everyone finishes and almost
 * nobody finishes clean.
 */
export function dailyQuestions(day = dailyNumber()) {
  const random = mulberry(day * 2654435761);
  const wanted = { easy: 2, medium: 2, hard: 1 };
  const picked = [];

  for (const [difficulty, count] of Object.entries(wanted)) {
    const pool = QUESTIONS.filter((q) => (q.difficulty ?? 'medium') === difficulty);
    const source = pool.length >= count ? pool : QUESTIONS;
    const taken = new Set();
    for (let i = 0; i < count && taken.size < source.length; i++) {
      let index;
      do {
        index = Math.floor(random() * source.length);
      } while (taken.has(index));
      taken.add(index);
      picked.push(source[index]);
    }
  }

  // Shuffle so the difficulty order is not the same shape every single day.
  for (let i = picked.length - 1; i > 0; i--) {
    const j = Math.floor(random() * (i + 1));
    [picked[i], picked[j]] = [picked[j], picked[i]];
  }
  return picked.slice(0, DAILY_COUNT);
}

/**
 * The grid people paste into a group chat.
 *
 * Squares and nothing else, deliberately: no question text, no answers, so
 * posting a result cannot spoil the puzzle for whoever reads it next. That
 * restraint is the entire reason this format spreads.
 */
export function shareText(day, results, streak = 0) {
  const grid = results.map((right) => (right ? '🟩' : '⬛')).join('');
  const score = results.filter(Boolean).length;
  const lines = [
    `Scripture Journey — Daily ${day}`,
    `${grid}  ${score}/${results.length}`,
  ];
  if (streak > 1) lines.push(`${streak} days running`);
  return lines.join('\n');
}

/**
 * Copy to the clipboard, falling back to a hidden textarea.
 *
 * `navigator.clipboard` is unavailable on plain http and in some in-app
 * browsers — exactly the places a shared link gets opened — so the old method
 * stays as a fallback rather than the share button silently doing nothing.
 */
export async function copyToClipboard(text) {
  try {
    await navigator.clipboard.writeText(text);
    return true;
  } catch {
    try {
      const area = document.createElement('textarea');
      area.value = text;
      area.setAttribute('readonly', '');
      area.style.position = 'fixed';
      area.style.opacity = '0';
      document.body.appendChild(area);
      area.select();
      const ok = document.execCommand('copy');
      document.body.removeChild(area);
      return ok;
    } catch {
      return false;
    }
  }
}
